import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import { Protection } from "@sportal/api";

import {
  getProfileCustomProtections,
  getProfileProtection,
} from "../../../../store/profiles/profiles.selectors";
import { changeProtection } from "../../../../store/profiles/profile.actions";
import { getCategories } from "../../../../store/root.selectors";
import { getCategoryNames } from "../../../../store/categories/categories.selectors";
import { getProtectionsList } from "../../../../store/protections/protections.selectors";
import { SBSharedProtection } from "../../../../store/protections/protections.types";
import { SBCategory } from "../../../../store/categories/categories.types";
import { useIntlTranslate } from "../../../../hooks";
import {
  ChangeProtectionCategoriesFn,
  ProtectionCategoryDetails,
} from "./protectionEditor/protectionEditor.types";
import { ProtectionStatus } from "./protectionEditor/protectionEditor.constants";
import {
  filterCategoriesByStatus,
  getCategoriesByName,
} from "./webFilters.helpers";
import { CategoriesByStatus, ProtectionLevel } from "./webFilters.types";
import { getBlockedCategories } from "./webFilters.selectors";

export const useFilterCategories = (
  categories: ProtectionCategoryDetails[],
  statusList = [ProtectionStatus.Allowed, ProtectionStatus.Blocked]
) => {
  const [input, setInput] = useState<string>("");

  const categoriesByStatus = filterCategoriesByStatus(categories, statusList);

  const filteredCategories = input
    ? getCategoriesByName(categoriesByStatus, input)
    : categoriesByStatus;

  return {
    input,
    setInput,
    filteredCategories,
  };
};

export const useProtectionLevels = (profileId: string): ProtectionLevel[] => {
  const translate = useIntlTranslate();

  const customCategories: string[] = useSelector(
    getProfileCustomProtections(profileId),
    _.isEqual
  );
  const protections: SBSharedProtection[] = useSelector(
    getProtectionsList,
    _.isEqual
  );

  return useMemo(
    () =>
      protections.map((option) => ({
        ...option,
        name: translate(option.name),
        value: option.name,
        isDisabled:
          option.name === Protection.Custom && _.isEmpty(customCategories),
      })),
    [protections, customCategories, translate]
  );
};

export const useChangeProtectionAction = (
  profileId: string
): ChangeProtectionCategoriesFn => {
  const dispatch = useDispatch();

  return (protection: Protection) => {
    dispatch(changeProtection(profileId, protection));
  };
};

export const useCategories = (profileId: string): Record<string, string[]> => {
  const customCategories: string[] = useSelector(
    getProfileCustomProtections(profileId),
    _.isEqual
  );

  const categoryNames: string[] = useSelector(getCategoryNames);

  return {
    categoryNames,
    customCategoryNames: customCategories,
  };
};

export const useWebFilterCategories = (
  profileId: string
): CategoriesByStatus => {
  const profileProtection: Protection = useSelector(
    getProfileProtection(profileId)
  );

  const categories: SBCategory[] = useSelector(getCategories, _.isEqual);

  const blockedNames: string[] = useSelector(
    getBlockedCategories(profileId, profileProtection),
    _.isEqual
  );

  const [blocked, allowed] = useMemo<[SBCategory[], SBCategory[]]>(
    () => _.partition(categories, ({ name }) => _.includes(blockedNames, name)),
    [categories, blockedNames]
  );

  return {
    allowed,
    blocked,
  };
};
